import { ColumnState } from "ag-grid-community";
import { create } from "zustand";
import { formatDateMonth, formatDateYear4 } from "src/utils/string/stringUtil";
import {
	defaultTQC2100_R01HeaderDto,
	TQC2100_R01DetailPivotDto,
	TQC2100_R01HeaderDto,
} from "./Dto_TQC2100_R01";

export interface TQC2100_R01Filter {
	compCd: string;
	inspectDt: string;
}

/** 현재 연월을 기본 조회기간으로 사용한다. */
export const defaultTQC2100_R01Filter: TQC2100_R01Filter = {
	compCd: "",
	inspectDt: `${formatDateYear4(new Date())}-${formatDateMonth(new Date())}`,
};

/** TQC2100_R01 화면의 검색, 헤더, 피벗 그리드 상태 정의 */
interface Store_TQC2100_R01 {
	TQC2100_R01Filter: TQC2100_R01Filter;
	TQC2100_R01PastFilter: TQC2100_R01Filter;
	setTQC2100_R01Filter: (value: Partial<TQC2100_R01Filter>) => void;
	setTQC2100_R01PastFilter: (value: TQC2100_R01Filter) => void;
	TQC2100_R01List: TQC2100_R01HeaderDto[];
	setTQC2100_R01List: (value: TQC2100_R01HeaderDto[]) => void;
	editTQC2100_R01: TQC2100_R01HeaderDto | null;
	setEditTQC2100_R01: (value: TQC2100_R01HeaderDto | null) => void;
	selectedTQC2100_R01: TQC2100_R01HeaderDto | undefined;
	setSelectedTQC2100_R01: (value: TQC2100_R01HeaderDto | undefined) => void;
	TQC2100_R01DetailPivotList: TQC2100_R01DetailPivotDto[];
	setTQC2100_R01DetailPivotList: (value: TQC2100_R01DetailPivotDto[]) => void;
	updateTQC2100_R01DetailPivot: (index: number, value: TQC2100_R01DetailPivotDto) => void;
	originalTQC2100_R01DetailPivotList: TQC2100_R01DetailPivotDto[];
	setOriginalTQC2100_R01DetailPivotList: (value: TQC2100_R01DetailPivotDto[]) => void;
	selectedTQC2100_R01DetailPivotList: string[];
	deletedTQC2100_R01DetailPivotList: string[];
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (value: ColumnState[] | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (value: number | null) => void;
	allCount: number;
	setAllCount: (value: number) => void;
	detailFetchTrigger: boolean;
	setDetailFetchTrigger: (value: boolean) => void;
	initData: () => void;
}

/** 화면을 닫거나 초기화해도 다른 MES 화면 상태와 섞이지 않도록 전용 store를 사용한다. */
export const useTQC2100_R01Store = create<Store_TQC2100_R01>((set) => ({
	TQC2100_R01Filter: defaultTQC2100_R01Filter,
	TQC2100_R01PastFilter: defaultTQC2100_R01Filter,
	setTQC2100_R01Filter: (value) =>
		set((state) => ({
			TQC2100_R01Filter: { ...state.TQC2100_R01Filter, ...value },
		})),
	setTQC2100_R01PastFilter: (value) => set({ TQC2100_R01PastFilter: value }),
	TQC2100_R01List: [],
	setTQC2100_R01List: (value) => set({ TQC2100_R01List: value }),
	editTQC2100_R01: defaultTQC2100_R01HeaderDto,
	setEditTQC2100_R01: (value) => set({ editTQC2100_R01: value }),
	selectedTQC2100_R01: undefined,
	// 헤더가 변경되면 이전 상세를 비우고 새 상세 조회를 요청한다.
	setSelectedTQC2100_R01: (value) =>
		set({
			selectedTQC2100_R01: value,
			TQC2100_R01DetailPivotList: [],
			originalTQC2100_R01DetailPivotList: [],
			detailFetchTrigger: true,
		}),
	TQC2100_R01DetailPivotList: [],
	setTQC2100_R01DetailPivotList: (value) =>
		set({ TQC2100_R01DetailPivotList: value }),
	updateTQC2100_R01DetailPivot: (index, value) =>
		set((state) => ({
			TQC2100_R01DetailPivotList: state.TQC2100_R01DetailPivotList.map((item) =>
				item.valueIndex === index ? value : item,
			),
		})),
	originalTQC2100_R01DetailPivotList: [],
	// 원본 행의 중첩 객체까지 복사해 수정 여부 비교 시 참조가 공유되지 않게 한다.
	setOriginalTQC2100_R01DetailPivotList: (value) =>
		set({
			originalTQC2100_R01DetailPivotList: value.map((item) => ({
				...item,
				dayValues: { ...item.dayValues },
				dayMeta: { ...item.dayMeta },
			})),
		}),
	selectedTQC2100_R01DetailPivotList: [],
	deletedTQC2100_R01DetailPivotList: [],
	detailColumnState: null,
	setDetailColumnState: (value) => set({ detailColumnState: value }),
	detailScrollTop: null,
	setDetailScrollTop: (value) => set({ detailScrollTop: value }),
	allCount: 0,
	setAllCount: (value) => set({ allCount: value }),
	detailFetchTrigger: false,
	setDetailFetchTrigger: (value) => set({ detailFetchTrigger: value }),
	// 액션바 초기화와 탭 종료 시 화면 업무 데이터를 기본값으로 되돌린다.
	initData: () =>
		set({
			TQC2100_R01Filter: defaultTQC2100_R01Filter,
			TQC2100_R01PastFilter: defaultTQC2100_R01Filter,
			TQC2100_R01List: [],
			selectedTQC2100_R01: undefined,
			editTQC2100_R01: defaultTQC2100_R01HeaderDto,
			TQC2100_R01DetailPivotList: [],
			originalTQC2100_R01DetailPivotList: [],
			allCount: 0,
			detailFetchTrigger: false,
			detailScrollTop: null,
		}),
}));
